import {
  TemporaryNumber,
  Server,
  Service,
  Location,
  Provider,
  SmspoolService,
  SmspoolCountry,
} from '../../types/api';

import { baseApi } from './baseApi';

// Get Servers
export interface GetServersResponse {
  status: 'success';
  message: string;
  data: {
    servers: Server[];
  };
}

// Get Services by Server
export interface GetServicesByServerParams {
  serverId: number;
}

export interface GetServicesByServerResponse {
  status: 'success';
  message: string;
  data: {
    serverId: number;
    serverName: string;
    services: Service[];
  };
}

// Get Locations by Server
export interface GetLocationsByServerParams {
  serverId: number;
  serviceId?: string;
}

export interface GetLocationsByServerResponse {
  status: 'success';
  message: string;
  data: {
    serverId: number;
    serverName: string;
    locationType: 'countries' | 'states' | 'areaCodes';
    locations: Location[];
  };
}

export interface GetProvidersByServerParams {
  serverId: number;
}

export interface GetProvidersByServerResponse {
  status: 'success';
  message: string;
  data: {
    serverId: number;
    providers: Provider[];
  };
}

// Legacy: Get Services (SMSPool only)
export interface GetServicesResponse {
  status: 'success';
  message: string;
  data: {
    services: SmspoolService[];
  };
}

// Legacy: Get Countries by Service
export interface GetCountriesByServiceParams {
  serviceId: string;
}

export interface GetCountriesByServiceResponse {
  status: 'success';
  message: string;
  data: {
    countries: SmspoolCountry[];
  };
}

// Purchase Number
export interface PurchaseNumberRequest {
  serverId?: number;
  serviceId: string;
  locationCode?: string;
  countryCode?: string;
  stateCode?: string;
  areaCode?: string;
  serviceName?: string;
  locationName?: string;
}

export interface PurchaseSummaryRequest {
  serverId: number;
  serviceId: string;
  locationCode?: string;
  stateCode?: string;
  areaCode?: string;
}

export interface PurchaseSummaryResponse {
  status: 'success';
  message: string;
  data: {
    serverId: number;
    service: {
      id: string;
      name: string;
    };
    location?: string;
    price: number;
    currency: string;
  };
}

export interface PurchaseNumberResponse {
  status: 'success';
  message: string;
  data: {
    temporaryNumber: TemporaryNumber;
  };
}

// Get Temporary Number
export interface GetTemporaryNumberResponse {
  status: 'success';
  message: string;
  data: {
    temporaryNumber: TemporaryNumber;
  };
}

// Check SMS Code
export interface CheckSmsCodeResponse {
  status: 'success';
  message: string;
  data: {
    temporaryNumberId: string;
    number: string;
    status: 'active' | 'expired' | 'refunded' | 'cancelled';
    smsCode: string | null;
    isCodeReceived: boolean;
    expiresAt: string;
  };
}

// Resend capability (GET) – check if number supports resend
export interface ResendCapabilityResponse {
  status: 'success';
  message: string;
  data: {
    resendSupported: boolean;
    serverId?: number;
    available?: boolean;
    resendsAvailable?: number;
    cost?: string;
    reusePrice?: string | number;
    expiresInHours?: number;
    message?: string;
  };
}

// Request resend (POST)
export interface ResendResponse {
  status: 'success';
  message: string;
  data: {
    temporaryNumber: TemporaryNumber;
    resend: {
      success: boolean;
      newOrderId?: string;
      charge?: number;
      expiresAt?: string;
    };
  };
}

// Get Temporary Numbers List
export interface GetTemporaryNumbersParams {
  status?: 'active' | 'expired' | 'refunded' | 'cancelled' | 'resendable' | 'waiting';
  page?: number;
  limit?: number;
}

export interface GetTemporaryNumbersResponse {
  status: 'success';
  message: string;
  data: {
    numbers: TemporaryNumber[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  };
}

export const temporaryNumbersApi = baseApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    // Get Available Servers
    getServers: builder.query<GetServersResponse, void>({
      query: () => ({
        url: '/temporary-numbers/servers',
        method: 'GET',
      }),
      providesTags: ['TemporaryNumbers'],
    }),

    // Get Services by Server
    getServicesByServer: builder.query<GetServicesByServerResponse, GetServicesByServerParams>({
      query: ({ serverId }) => ({
        url: `/temporary-numbers/servers/${serverId}/services`,
        method: 'GET',
      }),
      providesTags: ['TemporaryNumbers'],
    }),

    // Get Locations by Server
    getLocationsByServer: builder.query<
      GetLocationsByServerResponse,
      GetLocationsByServerParams
    >({
      query: ({ serverId, serviceId }) => {
        const params = new URLSearchParams();
        if (serviceId) params.append('serviceId', serviceId);

        const queryString = params.toString();
        return {
          url: `/temporary-numbers/servers/${serverId}/locations${queryString ? `?${queryString}` : ''}`,
          method: 'GET',
        };
      },
      providesTags: ['TemporaryNumbers'],
    }),

    // Legacy: Get Services (SMSPool)
    getServices: builder.query<GetServicesResponse, void>({
      query: () => ({
        url: '/temporary-numbers/services',
        method: 'GET',
      }),
      providesTags: ['TemporaryNumbers'],
    }),

    // Legacy: Get Countries by Service
    getCountriesByService: builder.query<
      GetCountriesByServiceResponse,
      GetCountriesByServiceParams
    >({
      query: ({ serviceId }) => ({
        url: `/temporary-numbers/services/${serviceId}/countries`,
        method: 'GET',
      }),
      providesTags: ['TemporaryNumbers'],
    }),

    // Purchase Temporary Number
    purchaseNumber: builder.mutation<PurchaseNumberResponse, PurchaseNumberRequest>({
      query: (data) => ({
        url: '/temporary-numbers/purchase',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['TemporaryNumbers', 'Balance', 'Transactions'],
    }),

    // Get providers by server (server 5 supports carriers)
    getProvidersByServer: builder.query<GetProvidersByServerResponse, GetProvidersByServerParams>({
      query: ({ serverId }) => ({
        url: `/temporary-numbers/servers/${serverId}/providers`,
        method: 'GET',
      }),
      providesTags: ['TemporaryNumbers'],
    }),

    // Get purchase summary before creating number
    getPurchaseSummary: builder.mutation<PurchaseSummaryResponse, PurchaseSummaryRequest>({
      query: (data) => ({
        url: '/temporary-numbers/purchase-summary',
        method: 'POST',
        body: data,
      }),
    }),

    // Get Temporary Number Details
    getTemporaryNumber: builder.query<GetTemporaryNumberResponse, string>({
      query: (temporaryNumberId) => ({
        url: `/temporary-numbers/${temporaryNumberId}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, temporaryNumberId) => [
        { type: 'TemporaryNumber', id: temporaryNumberId },
      ],
    }),

    // Check SMS Code
    checkSmsCode: builder.mutation<CheckSmsCodeResponse, string>({
      query: (temporaryNumberId) => ({
        url: `/temporary-numbers/${temporaryNumberId}/check-sms`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, temporaryNumberId) => [
        { type: 'TemporaryNumber', id: temporaryNumberId },
      ],
    }),

    // Resend capability – check if number supports resend (server 1 / SMSPool)
    getResendCapability: builder.query<ResendCapabilityResponse, string>({
      query: (temporaryNumberId) => ({
        url: `/temporary-numbers/${temporaryNumberId}/resend-capability`,
        method: 'GET',
      }),
      providesTags: (_result, _error, temporaryNumberId) => [
        { type: 'TemporaryNumber', id: temporaryNumberId },
      ],
    }),

    // Request resend – request new SMS code for the same number
    resend: builder.mutation<ResendResponse, string>({
      query: (temporaryNumberId) => ({
        url: `/temporary-numbers/${temporaryNumberId}/resend`,
        method: 'POST',
        body: {},
      }),
      invalidatesTags: (_result, _error, temporaryNumberId) => [
        { type: 'TemporaryNumber', id: temporaryNumberId },
        'TemporaryNumbers',
        'Balance',
        'Transactions',
      ],
    }),

    // Get Temporary Numbers List
    getTemporaryNumbers: builder.query<GetTemporaryNumbersResponse, GetTemporaryNumbersParams>({
      query: ({ status, page = 1, limit = 20 }) => {
        const params = new URLSearchParams();
        if (status) params.append('status', status);
        if (page) params.append('page', page.toString());
        if (limit) params.append('limit', limit.toString());

        const queryString = params.toString();
        return {
          url: `/temporary-numbers${queryString ? `?${queryString}` : ''}`,
          method: 'GET',
        };
      },
      providesTags: ['TemporaryNumbers'],
    }),
  }),
});

export const {
  useGetServersQuery,
  useGetServicesByServerQuery,
  useGetLocationsByServerQuery,
  useGetServicesQuery,
  useGetCountriesByServiceQuery,
  usePurchaseNumberMutation,
  useGetProvidersByServerQuery,
  useGetPurchaseSummaryMutation,
  useGetTemporaryNumberQuery,
  useCheckSmsCodeMutation,
  useGetResendCapabilityQuery,
  useResendMutation,
  useGetTemporaryNumbersQuery,
} = temporaryNumbersApi;

